/**
 * Static demo Q&A library. shareable, indexable, conversion-optimised.
 *
 * Each entry is a real Australian tenant scenario with a Compass answer.
 * Hard-coded (not DB-stored) so:
 * - The page renders instantly with content (good for SEO and link previews)
 * - We never hit the AI budget for these
 * - We can curate quality (these are showpiece answers)
 *
 * Add a new entry by appending an object to DEMO_QA. The slug becomes the URL:
 * tenanttrail.net/q/<slug>
 *
 * When the founder generates a new high-quality answer in /ask, copy the
 * answer here, write a 1-line teaser + ~120 char meta-description, and ship.
 */

export const DEMO_QA = [
 {
 slug: "nsw-open-homes-during-sale",
 state: "NSW",
 title: "Can my agent force weekly open homes when the property is for sale?",
 teaser: "23 year-old NSW renter, landlord selling, agent says open homes every Saturday. must she comply?",
 metaDescription: "NSW tenant rights when landlord sells. Open home inspections, photography access, s 53 of the Residential Tenancies Act 2010 explained.",
 persona: "Asked anonymously in a NSW tenant support Facebook group",
 question: "I'm a 23-year-old woman living alone in NSW. My landlord has put the property up for sale during my lease. The agent is pushing weekly Saturday 9am open homes until sold and says I can't refuse. I'm not refusing access. I just want private appointments with registered buyers instead of open homes for safety reasons. I also declined one photography slot but offered alternatives the following week, and now the agent says I 'refused access'. What are my actual rights under the NSW Residential Tenancies Act 2010?",
 answer: `**Short answer:** You have strong grounds to push back. The agent is misrepresenting both the law and what you have done.

### 1. Open homes are NOT automatically permitted

Under **s 53 of the Residential Tenancies Act 2010 (NSW)**, sale inspections require tenant agreement on times. If you and the agent cannot agree, either party may apply to NCAT under **s 53(5)** for orders limiting frequency or manner of entry. The Act does NOT give landlords a blanket right to hold weekly open homes. each inspection requires fresh notice and your reasonable cooperation.

For photography, **s 53A** requires your **written consent**. You can refuse if photos would identify you or your possessions, and you are entitled to copies of any photos taken.

### 2. "Reasonable time" and your safety concerns

NCAT consistently holds that what is "reasonable" depends on the tenant's circumstances. Living alone as a young woman is a recognised privacy/safety issue. Tenants can reasonably request **pre-registered buyers only** with ID confirmed by the agent, rather than unvetted walk-ins. The agent saying you "can't refuse" open homes is **wrong**.

### 3. You did NOT "refuse access"

Refusing access means saying "no, you cannot enter at all." You did the opposite. you declined one specific time slot for photography and offered alternative dates the following week. That is **negotiating access in good faith**, which is exactly what the Act contemplates.

### 4. What to do today

**Step 1. Put it in writing.** Email the agent:

> *"I confirm I am not refusing access for sale purposes. I am happy to accommodate private inspections with pre-registered buyers at reasonable times, with 7 days written notice. I am not consenting to weekly open homes due to safety and privacy concerns (I am a young woman living alone). For photography, my written consent is required under s 53A. I am available [specific dates/times]. Please confirm."*

**Step 2. If they schedule an open home anyway**, file an application with NCAT under s 53(5). Filing fee around \$50. The tribunal can make urgent interim orders.

**Step 3. Document everything**: screenshot every notice, log who entered and when, photograph the unit before and after.

### 5. Retaliation protection

**s 115** prohibits retaliatory conduct (eviction notice, rent rise, harassment) within 6 months of a tenant exercising rights. If they issue a termination notice shortly after you assert these rights, NCAT can set it aside.

### Free legal help

Contact the **Tenants' Union of NSW**. free phone advice on **1800 251 101** (tenants.org.au). They can help draft the NCAT application.`,
 citations: ["s 53 RTA 2010 (NSW)", "s 53(5) RTA 2010 (NSW)", "s 53A RTA 2010 (NSW)", "s 50 RTA 2010 (NSW)", "s 115 RTA 2010 (NSW)"],
 },
 {
 slug: "qld-bond-not-returned",
 state: "QLD",
 title: "My QLD landlord is refusing to return my bond. what now?",
 teaser: "4-week wait, no response from RTA, agent claims 'damage' you don't recognise. the QCAT pathway.",
 metaDescription: "How to get your bond back in Queensland. RTA dispute resolution, QCAT applications, s 125 of the Residential Tenancies and Rooming Accommodation Act 2008.",
 persona: "Common QLD scenario",
 question: "I moved out of my QLD rental three weeks ago. The agent has not returned my bond and now claims there is carpet damage I do not recognise. they showed me a photo but the marks were there when I moved in. The bond is \$1,920. How do I get it back?",
 answer: `**Short answer:** Lodge a Form 4 dispute with the RTA today, and if not resolved within 7 days, apply to QCAT. Your entry condition report is your most powerful weapon here.

### 1. The QLD bond return process

Under **s 125 of the Residential Tenancies and Rooming Accommodation Act 2008 (QLD)**:

- **You and the lessor must agree on the bond split** using a Refund of Rental Bond (Form 4).
- If the lessor disputes any of it, **they must apply to QCAT within 7 days** of receiving your form. otherwise the RTA must pay out per your application.
- If you both agree the amount, the RTA returns it within 2–3 business days.

### 2. Your evidence checklist

Pull together:
- **Entry Condition Report (Form 1a)**. the gold standard. Compare every "damage" claim against what was already noted on entry. Carpet marks pre-existing on the entry report = lessor cannot claim them.
- **Photos with EXIF timestamps** from move-in day and move-out day (if you have them).
- **Exit Condition Report** if you filled one out.
- **Receipts** for professional cleaning or carpet steam-cleaning.
- **Emails/SMS** discussing the bond.

### 3. Step-by-step

**Today**. Submit Form 4 (Refund of Rental Bond) to the RTA online (rta.qld.gov.au) claiming **the full \$1,920**. List the lessor's email/address. The RTA notifies them automatically.

**Day 7**. If the lessor does not apply to QCAT, the RTA pays you the full bond.

**If they DO apply to QCAT**. you will receive a hearing notice (usually 4–8 weeks out). Prepare your evidence bundle:
- Index page listing every document
- Annex 1: entry condition report
- Annex 2: move-in photos (with EXIF)
- Annex 3: move-out photos
- Annex 4: any communications
- Annex 5: receipts

Hearings are informal. The Member will ask you to walk through your evidence. Stay factual, point to the entry report for pre-existing marks.

### 4. What QCAT will likely do

The lessor bears the burden of proving damage was caused by the tenant. If your entry condition report notes the carpet marks, **you win**. If it says "good condition" or is blank, it becomes evidence-heavy and harder.

### Free legal help

**Tenants Queensland**. free phone advice on **1300 744 263**. They can help you prepare for QCAT.`,
 citations: ["s 125 RTRA Act 2008 (QLD)", "s 419 RTRA Act 2008 (QLD)"],
 },
 {
 slug: "vic-no-heater-winter",
 state: "VIC",
 title: "My VIC rental has no working heater in winter. what can I do?",
 teaser: "Melbourne tenant, mid-July, agent dragging feet on a broken heater. Minimum standards + urgent repairs.",
 metaDescription: "Victorian tenant rights for urgent repairs and minimum standards. Working heater requirements under the RTA 1997 (VIC) Schedule 4.",
 persona: "Common VIC scenario",
 question: "My ducted heating in Melbourne stopped working in early July. I reported it to the agent two weeks ago via email but they keep saying 'we are getting quotes'. It is now 5°C overnight and my kids are sleeping in jumpers. What are my actual rights?",
 answer: `**Short answer:** A working heater in the main living room is a **minimum standard** under Victorian law. The agent has no excuse for two-week delays. you can arrange an urgent repair yourself and claim it back, or apply to VCAT for an urgent hearing.

### 1. Minimum standards

Under **Schedule 4 of the Residential Tenancies Act 1997 (VIC)** (in force since 29 March 2021):

> "There must be a fixed heater in good working order in the main living area, with a 2-star energy rating or higher, capable of heating the room to a comfortable temperature."

If the heater does not work, the rental does not meet minimum standards. **You did not have to accept the rental this way**. the landlord is in breach from day one of non-compliance.

### 2. Urgent repairs

Under **s 72** of the RTA 1997 (VIC), heating failure in winter is classed as an **urgent repair**. The process:

1. **You must give the landlord/agent a reasonable opportunity to fix it**. usually 24-48 hours, NOT two weeks.
2. **If they do not act**, you may arrange the repair yourself with a qualified contractor up to a limit of \$2,500.
3. The landlord must reimburse you within 7 days of receiving the invoice.

### 3. What to do today

**Step 1. Send a written ultimatum** (email + text, keep copies):

> *"Further to my email of [date], the ducted heating has been inoperable for [X] days. This is an urgent repair under s 72 RTA 1997 (VIC), and the property is not meeting the minimum standard for fixed heating in the main living area (Schedule 4). I require the heater to be repaired by [date 48 hours from now]. If it is not, I will arrange a qualified contractor under s 72 and recover the cost from you under s 73."*

**Step 2. If still no action**, get a quote from a licensed contractor. Arrange the repair (within the \$2,500 cap). Send the invoice to the agent for reimbursement within 7 days.

**Step 3. Apply to VCAT** for an urgent hearing if the agent disputes the bill or you cannot afford to front the cost. Use the **Tenancy Application for Urgent Repairs** form on vcat.vic.gov.au.

### 4. Rent reduction

You may also be entitled to a **rent reduction** under **s 78** for the period the heating was broken, on the basis the premises were not fit for their stated purpose. VCAT routinely awards 10-30% rent reductions for unaddressed minimum-standard breaches.

### Free legal help

**Tenants Victoria**. free advice line: **(03) 9416 2577** (tenantsvic.org.au).`,
 citations: ["Schedule 4 RTA 1997 (VIC)", "s 72 RTA 1997 (VIC)", "s 73 RTA 1997 (VIC)", "s 78 RTA 1997 (VIC)"],
 },
 {
 slug: "agent-entering-without-notice",
 state: "ALL",
 title: "My agent let themselves in without notice. is that allowed?",
 teaser: "Came home from work, found the agent had been inside taking photos. What now?",
 metaDescription: "Tenant rights when an agent enters without notice. Documentation, complaint pathways, breach of quiet enjoyment.",
 persona: "Common scenario across all Australian states",
 question: "I came home from work today and my neighbour told me the agent had let themselves into my place at lunchtime to take photos for a sale listing. I never received notice. I am a private person and I am furious. Is this allowed?",
 answer: `**Short answer:** No. Entry without notice is unlawful in every Australian state and territory. Document it immediately and lodge a formal complaint. this is a clear breach of quiet enjoyment and likely a fineable offence.

### 1. The universal rule

Every state's Residential Tenancies Act requires the landlord or agent to give **written notice** before entry, in most cases with a defined minimum period (typically 7 days for general inspections, 24-48 hours for repairs). Entry without proper notice is unlawful EXCEPT in emergencies (fire, flood, suspected unlawful activity).

In your case:
- **NSW:** s 55 + s 57 RTA 2010. entry by agent requires both proper notice AND written authorisation produced to the tenant if present
- **QLD:** s 192–193 RTRA Act 2008. Entry Notice Form 9 required
- **VIC:** s 86 RTA 1997. 7 days notice for general entry; entry without notice is a fineable offence
- **WA / SA / TAS / NT / ACT:** equivalent provisions in each Act

### 2. What to do today

**Step 1. Document everything:**
- Note the date, time and source (your neighbour's account)
- Ask the neighbour to send a written statement (text/email is fine)
- Check your home for any signs (moved items, photos posted online)
- If they took photos and listed them, **screenshot the listing** as evidence of entry

**Step 2. Write to the landlord and agent** (formal, not friendly):

> *"On [date] at approximately [time], your agent entered my premises at [address] without notice and took photographs. This is unlawful entry under [your state's RTA section] and a clear breach of my right to quiet enjoyment. I require: (1) written acknowledgement of the unlawful entry; (2) immediate removal of any photographs taken without consent; (3) confirmation that no further entry will occur without proper written notice; and (4) compensation for the breach of quiet enjoyment."*

**Step 3. Lodge a complaint** with your state's regulator:
- **NSW:** NSW Fair Trading (13 32 20)
- **QLD:** RTA (1300 366 311)
- **VIC:** Consumer Affairs Victoria (1300 558 181)
- **WA:** DMIRS (1300 304 054)
- **SA:** Consumer and Business Services (131 882)

**Step 4. If serious or repeated**, apply to your tribunal for orders restraining further entry and compensation. Tribunal fees are typically \$50–\$80 and the bar to win on these facts is low.

### 3. Compensation

Tribunals routinely award compensation for unlawful entry. usually 1-2 weeks rent, sometimes more if the entry was particularly egregious (photographing personal items, repeated incidents, or where the tenant has previously asked the agent to respect notice requirements).

### Free legal help

Contact your state's free Tenants' Union. they handle this kind of complaint every day.`,
 citations: ["s 55, s 57 RTA 2010 (NSW)", "s 192-193 RTRA Act 2008 (QLD)", "s 86 RTA 1997 (VIC)"],
 },
 {
 slug: "vca-vcat-mould-claim",
 state: "VIC",
 title: "There's mould in my VIC rental and the agent keeps saying it's my fault",
 teaser: "Black mould in the bathroom, agent blames 'tenant lifestyle', VCAT and rent reduction options.",
 metaDescription: "Mould in a Victorian rental property. Tenant rights, urgent repairs, VCAT compensation under the RTA 1997 (VIC).",
 persona: "Common scenario",
 question: "My VIC rental has visible black mould in the bathroom and along one bedroom wall. The agent keeps saying it is my fault for not opening windows. I do open them. The bathroom has no exhaust fan. What can I do?",
 answer: `**Short answer:** Lack of an exhaust fan in a wet area is almost certainly a minimum-standards breach. The agent's "tenant lifestyle" line is a standard deflection that does not survive a tribunal. Document it and either repair-and-recover or apply to VCAT.

### 1. The minimum-standards angle

Under **Schedule 4 of the RTA 1997 (VIC)**:

> "A bathroom must have a window that opens or a working exhaust fan."

If your bathroom has no exhaust fan and no openable window, the rental fails the minimum standards from day one. The landlord is in breach **regardless of mould**, and you may be entitled to a rent reduction back to the start of the tenancy.

### 2. Mould as an urgent repair

Black mould is a health hazard. Under **s 72**, untreated mould is an **urgent repair** if it is making the premises unsafe or unhealthy. Get evidence:
- Photos with EXIF timestamps from multiple angles
- Photos of you opening the window (rebut the "you don't ventilate" argument)
- If you have asthma or respiratory issues, a GP letter linking symptoms to mould exposure

### 3. What to do today

**Step 1. Written request for repair:**

> *"Mould is present in the bathroom and bedroom wall (see photos attached). The bathroom has no exhaust fan, which is a minimum-standards breach under Schedule 4 RTA 1997 (VIC). Mould is an urgent repair under s 72. I require: (1) professional mould remediation; (2) installation of a compliant exhaust fan; and (3) compensation/rent reduction for the period the property has failed minimum standards. Please confirm by [date. 7 days]."*

**Step 2. If they refuse or delay**, apply to **Consumer Affairs Victoria** (free dispute resolution). they will mediate. If mediation fails, apply to **VCAT** for:
- Order requiring repair (s 78)
- Rent reduction (s 78)
- Compensation for damage to your belongings, health costs, alternative accommodation costs

### 4. What VCAT will likely do

VCAT regularly awards:
- Full mould remediation at landlord cost
- Installation of compliant exhaust fan
- 10-40% rent reduction depending on severity and duration
- Compensation for any belongings damaged by mould
- Costs of GP visits if you can link them to mould

The "tenant lifestyle" argument almost always fails when there is no working ventilation.

### Free legal help

**Tenants Victoria**. (03) 9416 2577. they can help you prepare the VCAT application.`,
 citations: ["Schedule 4 RTA 1997 (VIC)", "s 72 RTA 1997 (VIC)", "s 78 RTA 1997 (VIC)"],
 },
];

export function getDemoQA(slug) {
 return DEMO_QA.find(q => q.slug === slug);
}
